import { isEqual, isUndefined, omitBy } from 'lodash'
import { SetStateAction, useState } from 'react'

import { KeyOf } from '@/models'

// biome-ignore lint/suspicious/noExplicitAny: `any` (not `unknown`) lets concrete filter types without an index signature (e.g. `GetMarketShopProductsRequest`) satisfy `T extends FiltersType` bidirectionally across every `useFilters` caller
export type FiltersType = Record<PropertyKey, any>

// Note: Base keys for filters. Rest of the keys are considered FILTER_KEYS
// Note: BASE_KEYS are used to keep filters when resetting filters
export const PAGINATION_KEYS = ['skip', 'limit']
export const SORT_KEYS = ['sortBy', 'sortOrder']
export const SEARCH_KEYS = ['search', 'fromDate', 'toDate']
export const NON_RESETABLE_KEYS = ['status']
export const BASE_FILTERS_KEYS = [
  ...PAGINATION_KEYS,
  ...SORT_KEYS,
  ...SEARCH_KEYS,
  ...NON_RESETABLE_KEYS,
]

type HandlerProps<T extends FiltersType> = {
  disposeKeys?: boolean
  keepKeys?: Array<KeyOf<T> | string>
  // Note: Opts keys out of the always-kept set (pagination + NON_RESETABLE_KEYS) so they are reset too
  resetKeys?: Array<KeyOf<T> | string>
}

const mergeFilters = <T extends FiltersType>(prevFilters: T, newFilters: Partial<T>): T => {
  const newFiltersState = { ...prevFilters } as FiltersType
  if ('skip' in newFiltersState && typeof newFiltersState.skip === 'number') {
    newFiltersState.skip = 0
  }
  return { ...newFiltersState, ...newFilters } as T
}

export interface UseFiltersProps<T extends FiltersType> {
  initialState: T
  /**
   * NOTE: controlledState is used to pass actual filters value from local storage, query params (or other source) to the hook
   * e.g., control filters externally
   */
  controlledState?: T
  onFiltersChange?: (filters: T) => void
}

// TODO: Write a unified filters mapping method which will be used to pass params to the tanstack query.
// We don't need all functions created now
// IGOR's comment: unification is not needed, we have to remove functions for building the params for API and always build these params manually in the fetch hook.
export const useFilters = <T extends FiltersType>({
  initialState,
  controlledState,
  onFiltersChange,
}: UseFiltersProps<T>) => {
  const isControlled = controlledState != null && !!onFiltersChange

  // Note: even when controlled, we render from local state. Controlled sources like URL query
  // params commit asynchronously (react-router wraps navigation updates in `startTransition`),
  // so reading straight from `controlledState` makes text inputs lag a keystroke behind - React
  // restores the stale DOM value and the caret jumps to the end. Local state updates
  // synchronously; it's re-synced below whenever the external source actually changes
  // (back/forward navigation, links, etc.).
  const [filtersState, setFiltersState] = useState(() =>
    isControlled ? controlledState : initialState,
  )
  const [prevControlledState, setPrevControlledState] = useState(controlledState)
  if (isControlled && !isEqual(controlledState, prevControlledState)) {
    setPrevControlledState(controlledState)
    setFiltersState(controlledState)
  }
  const actualFiltersState = filtersState

  const handleSetStoredFilters = (dispatch: SetStateAction<T>) => {
    const newFilters = typeof dispatch === 'function' ? dispatch(actualFiltersState) : dispatch
    // Note: store what the controlled source will read back, not the raw write. URL-backed
    // sources drop `undefined` keys and fill them back in from `initialState`, so a write like
    // clearing sort while already on the default sort leaves the URL unchanged - no re-sync
    // would fire, and local state would otherwise drift from the URL (e.g. unsorted vs default).
    setFiltersState(
      isControlled ? ({ ...initialState, ...omitBy(newFilters, isUndefined) } as T) : newFilters,
    )
    onFiltersChange?.(newFilters)
  }

  const handleResetFilters = ({ keepKeys = [], resetKeys = [] }: HandlerProps<T> = {}) => {
    const exceptionKey = new Set([...PAGINATION_KEYS, ...NON_RESETABLE_KEYS, ...keepKeys])
    for (const key of resetKeys) exceptionKey.delete(key)
    // biome-ignore lint/suspicious/noExplicitAny: `Partial<T>` can only be indexed for reading on a generic `T`; the accumulator is mutated by key below
    const newFilters = Object.entries(actualFiltersState).reduce<any>((acc, [key, value]) => {
      if (exceptionKey.has(key)) {
        acc[key] = value
      } else if (initialState[key] != null) {
        acc[key] = initialState[key]
      }
      return acc
    }, {})
    handleSetStoredFilters(newFilters)
  }

  const handleSetFilters = (dispatch: SetStateAction<T>, disposePagination = false) => {
    handleSetStoredFilters((prevFilters) => {
      const newFilters = typeof dispatch === 'function' ? dispatch(prevFilters) : dispatch

      if (disposePagination) {
        delete newFilters.skip
      }

      return mergeFilters(prevFilters, newFilters)
    })
  }

  const setFilter = <K extends keyof T>(key: K, value: T[K]) => {
    handleSetStoredFilters((prevFilters) =>
      // biome-ignore lint/suspicious/noExplicitAny: `{ [key]: value }` can't be proven to structurally match the generic `Partial<T>` at this call site
      mergeFilters(prevFilters, { [key]: value } as any),
    )
  }

  const getFilter = <K extends keyof T>(key: K): T[K] => {
    return actualFiltersState[key]
  }

  return {
    filters: actualFiltersState,
    getFilter,
    handleResetFilters,
    initialState,
    setFilter,
    setFilters: handleSetFilters,
  }
}
